// pages/profile/Update.jsx
import React, { useState } from 'react';
import { useAuth, api } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { 
  FiUser, FiMail, FiPhone, FiLock, FiSave, FiEdit2,
  FiAlertCircle, FiCheckCircle, FiSend, FiX
} from 'react-icons/fi';

function UpdateProfile() {
  const { user, token } = useAuth();
  
  // Profile States
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    username: user?.username || '',
  });
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(user?.emailVerified || false);
  
  // Email Change States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeTimer, setCodeTimer] = useState(0);
  
  // Password Change States
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Update Profile
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put('/api/auth/update', {
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
      });
      
      if (response.data.success) {
        toast.success('Profile updated successfully!');
        const updatedUser = { ...user, 
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
        };
        localStorage.setItem('dev_user', JSON.stringify(updatedUser));
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error(response.data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.status === 404) {
        toast.error('Profile update endpoint not available. Please try again later.');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  // Send Email Verification Code
  const sendVerificationCode = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setEmailLoading(true);
    try {
      const response = await api.post('/api/auth/send-verification', { 
        email: newEmail,
        type: 'email_change'
      });
      
      if (response.data.success) {
        toast.success('Verification code sent to your new email!');
        setCodeSent(true);
        setCodeTimer(60);
        const interval = setInterval(() => {
          setCodeTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error(response.data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification:', error);
      if (error.response?.status === 404) {
        toast.error('Email verification not available');
      } else {
        toast.error('Failed to send verification code');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // Change Email with Verification
  const handleEmailChange = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!emailVerificationCode || emailVerificationCode.length < 4) {
      toast.error('Please enter the verification code');
      return;
    }

    const password = prompt('Please enter your password to confirm:');
    if (!password) {
      toast.error('Password is required to change email');
      return;
    }

    setEmailLoading(true);
    try {
      const response = await api.post('/api/auth/change-email', {
        email: newEmail,
        code: emailVerificationCode,
        password: password,
      });

      if (response.data.success) {
        toast.success('Email updated successfully!');
        setFormData({ ...formData, email: newEmail });
        setShowEmailModal(false);
        setNewEmail('');
        setEmailVerificationCode('');
        setCodeSent(false);
        const updatedUser = { ...user, email: newEmail };
        localStorage.setItem('dev_user', JSON.stringify(updatedUser));
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error(response.data.error || 'Failed to update email');
      }
    } catch (error) {
      console.error('Error changing email:', error);
      if (error.response?.status === 404) {
        toast.error('Email change not available');
      } else if (error.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error('Failed to update email');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // Change Password
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.data.success) {
        toast.success('Password changed successfully!');
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        toast.error(response.data.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 404) {
        toast.error('Password change endpoint not available');
      } else if (error.response?.status === 401) {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>✏️ Update Profile</h2>

      {/* Profile Form */}
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              <FiUser style={{ marginRight: '6px' }} />
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              disabled
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            />
            <small style={{ color: 'var(--text-muted)' }}>Username cannot be changed</small>
          </div>

          <div className="form-group">
            <label>
              <FiUser style={{ marginRight: '6px' }} />
              Full Name
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="form-group">
            <label>
              <FiMail style={{ marginRight: '6px' }} />
              Email
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="email"
                value={formData.email}
                disabled
                style={{ flex: 1, opacity: 0.6, cursor: 'not-allowed' }}
              />
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => setShowEmailModal(true)}
                style={{ whiteSpace: 'nowrap' }}
              >
                <FiEdit2 /> Change
              </button>
            </div>
            <small style={{ color: 'var(--text-muted)' }}>
              {emailVerified ? '✅ Email verified' : '⚠️ Email not verified'}
            </small>
          </div>

          <div className="form-group">
            <label>
              <FiPhone style={{ marginRight: '6px' }} />
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phoneNumber || ''}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              placeholder="Your phone number (optional)"
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <FiSave /> {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Password Change */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <FiLock style={{ marginRight: '8px' }} />
            Change Password
          </span>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
          Update your password to keep your account secure.
        </p>
        <button className="btn btn-outline" onClick={() => setShowPasswordModal(true)}>
          <FiLock /> Change Password
        </button>
      </div>

      {/* Email Change Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3><FiMail /> Change Email</h3>
              <button className="modal-close" onClick={() => setShowEmailModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="form-group">
              <label>New Email Address</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                disabled={codeSent}
              />
            </div>

            {!codeSent ? (
              <button 
                className="btn btn-primary" 
                onClick={sendVerificationCode}
                disabled={emailLoading || !newEmail}
                style={{ width: '100%', marginBottom: '12px' }}
              >
                <FiSend /> {emailLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            ) : (
              <>
                <div className="form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '4px' }}
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Code sent to {newEmail}
                    {codeTimer > 0 && ` • Resend in ${codeTimer}s`}
                    {codeTimer === 0 && (
                      <button 
                        className="btn btn-outline" 
                        onClick={sendVerificationCode}
                        style={{ marginLeft: '8px', padding: '2px 12px', fontSize: '12px' }}
                      >
                        Resend
                      </button>
                    )}
                  </small>
                </div>

                <button 
                  className="btn btn-success" 
                  onClick={handleEmailChange}
                  disabled={emailLoading || !emailVerificationCode}
                  style={{ width: '100%' }}
                >
                  <FiCheckCircle /> {emailLoading ? 'Verifying...' : 'Verify & Change Email'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3><FiLock /> Change Password</h3>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label>Current Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ 
                      ...passwordData, 
                      currentPassword: e.target.value 
                    })}
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ 
                      ...passwordData, 
                      newPassword: e.target.value 
                    })}
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    confirmPassword: e.target.value 
                  })}
                  placeholder="Confirm new password"
                  required
                />
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <small style={{ color: 'var(--danger)' }}>
                    <FiAlertCircle /> Passwords do not match
                  </small>
                )}
                {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                  <small style={{ color: 'var(--success)' }}>
                    <FiCheckCircle /> Passwords match
                  </small>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={passwordLoading}
                  style={{ flex: 1 }}
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline" 
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }
        
        .modal {
          background: var(--bg-card);
          border-radius: var(--radius);
          padding: 32px;
          max-width: 480px;
          width: 100%;
          border: 1px solid var(--border-color);
          max-height: 90vh;
          overflow-y: auto;
          animation: modalIn 0.2s ease;
        }
        
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .modal-header h3 {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .modal-close {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 20px;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.3s ease;
        }
        
        .modal-close:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }
        
        .btn-success {
          background: var(--success);
          color: white;
        }
        
        .btn-success:hover {
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
}

export default UpdateProfile;